import { useContext, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  useQueries,
  useQuery,
  useQueryClient,
  useMutation,
} from '@tanstack/react-query';
import Select from 'react-select';

import { Loader, Message } from '@components';
import { GlobalContext } from '@contexts';
import { DatamodelFormField, FormError, HelpText } from '@Forms/fields.jsx';
import { fieldIsVisible } from '@Forms/utils.js';
import { datamodelIdFromType } from '@helpers';
import {
  datamodelQuery,
  datamodelsQuery,
  siteMutation,
  siteQuery,
  sitesQuery,
  themeQuery,
} from '@queries';

import { BulkUpdateProgress } from './BulkUpdateProgress.jsx';

const SUBMIT_MODES = {
  SAVE_DRAFT: 'save_draft',
  PUBLISH_OR_DRAFT: 'publish_or_draft',
  PUBLISH_ALL: 'publish_all',
};
// how the bulk edit should be applied per-site, mirroring the draft_action
// contract used elsewhere (ManageContentForm): 'save' always creates/updates
// a draft; a plain PUT with no draft_action is an immediate publish; and
// draft_action:'publish' pushes an existing draft (whatever's in it) live.
const SUBMIT_MODE_OPTIONS = [
  {
    value: SUBMIT_MODES.SAVE_DRAFT,
    label: 'Save as draft',
    helpText:
      'Drafts will be created if none exist, or the existing draft will be saved.',
  },
  {
    value: SUBMIT_MODES.PUBLISH_OR_DRAFT,
    label: 'Publish changes, or save to draft if in draft',
    helpText: 'Publish changes, or save to draft if in draft.',
  },
  {
    value: SUBMIT_MODES.PUBLISH_ALL,
    label: 'Publish all changes',
    helpText:
      'Publish all changes, including publishing any existing drafts in the process.',
  },
];

// portal these Selects' menus to the body and above everything else - a
// Simple HTML field's tinymce toolbar (z-index: 2, from the vendor skin CSS)
// otherwise sits on top of a menu rendered in place, since react-select's
// own default menu z-index is only 1
const SELECT_MENU_PORTAL_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const SUBMIT_MODE_WARNINGS = {
  [SUBMIT_MODES.SAVE_DRAFT]: {
    title: 'This will overwrite draft values',
    message:
      'Saving as draft will replace the current draft value of any fields you select, on every website you select, wherever a value already exists. Published content will not change until the draft is published.',
  },
  [SUBMIT_MODES.PUBLISH_OR_DRAFT]: {
    title: 'This will overwrite existing values',
    message:
      'Websites with drafts will have the selected draft values replaced. Websites without drafts will have the selected published values replaced immediately. This cannot be undone.',
  },
  [SUBMIT_MODES.PUBLISH_ALL]: {
    title: 'This will publish and overwrite existing values',
    message:
      'Publishing all changes will replace the current value of any fields you select, on every website you select, and will publish any existing drafts in the process. This cannot be undone.',
  },
};

export const BulkUpdateWebsite = ({ businessID }) => {
  const queryClient = useQueryClient();
  const globals = useContext(GlobalContext);

  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [selectedFieldSlugs, setSelectedFieldSlugs] = useState([]);
  const [submitMode, setSubmitMode] = useState('');
  const [submitModeError, setSubmitModeError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [progressRows, setProgressRows] = useState([]);
  // captured at submit time so the progress view's reminder still reflects
  // this run even after onStartOver resets submitMode for the next one
  const [submittedModeLabel, setSubmittedModeLabel] = useState('');

  // all non-template sites in this project, with their theme_id, so the
  // template/site pickers below can be derived without extra endpoints
  const { data: sitesData, isPending: sitesIsPending } = useQuery(
    sitesQuery({
      business_id: businessID,
      deleted: 0,
      limit: 1000,
      type: 'default',
      _fields: ['id', 'theme_id', 'display_url', 'base_directory'].join(','),
    }),
  );
  const sites = useMemo(() => sitesData?.objects || [], [sitesData]);

  // website templates - type:'template' sites; each has a theme_id that
  // websites using that template share
  const { data: templatesData, isPending: templatesIsPending } = useQuery(
    sitesQuery({
      deleted: 0,
      limit: 1000,
      order_by: 'title',
      type: 'template',
      _fields: ['id', 'title', 'theme_id'].join(','),
    }),
  );
  const templates = useMemo(
    () => templatesData?.objects || [],
    [templatesData],
  );
  const templateOptions = templates.map((template) => ({
    value: template.id,
    label: template.title,
  }));
  const selectedTemplate = templates.find(
    (template) => template.id === selectedTemplateId,
  );

  // sites belonging to the selected template (same theme_id)
  const siteOptions = useMemo(
    () =>
      sites
        .filter((s) => s.theme_id === selectedTemplate?.theme_id)
        .map((s) => ({
          value: s.id,
          label: s.display_url || s.base_directory,
        })),
    [sites, selectedTemplate],
  );

  // the "site custom fields" datamodel tied to the selected template's
  // theme - same resolution order as the single-site Manage content page:
  // prefer the theme's own datamodel_id, fall back to the global
  // site_custom_fields datamodel if the theme doesn't have one assigned
  const { data: selectedThemeData, isPending: themeIsPending } = useQuery(
    themeQuery(selectedTemplate?.theme_id, {
      enabled: !!selectedTemplate?.theme_id,
    }),
  );
  const { data: datamodelsData } = useQuery(
    datamodelsQuery(
      {},
      {
        enabled:
          !!selectedTemplate &&
          !themeIsPending &&
          !selectedThemeData?.datamodel_id,
      },
    ),
  );
  const datamodelID = useMemo(() => {
    if (!selectedTemplate || themeIsPending) {
      return null;
    }
    return selectedThemeData?.datamodel_id
      ? selectedThemeData.datamodel_id
      : datamodelsData?.objects
        ? datamodelIdFromType(datamodelsData.objects, 'site_custom_fields')
        : null;
  }, [selectedTemplate, themeIsPending, selectedThemeData, datamodelsData]);

  const { data: datamodelData, isPending: datamodelIsPending } = useQuery(
    datamodelQuery(datamodelID, { enabled: !!datamodelID }),
  );
  console.log('globals.group_ids:', globals.group_ids);

  const restrictCustomFields = !!(
    globals &&
    globals.group_settings &&
    globals.group_settings.restrict_custom_fields
  );
  const visibleFields = (datamodelData?.fields || []).filter((field) => {
    if (!restrictCustomFields) {
      return true;
    }
    console.log('Field.groups:', field.groups);
    return fieldIsVisible(field, globals.group_ids);
  });

  const fieldOptions = visibleFields.map((field) => ({
    value: field.slug,
    label: field.label,
  }));
  const selectedFields = visibleFields.filter((field) =>
    selectedFieldSlugs.includes(field.slug),
  );

  // related datamodels needed by any selected related_model_list fields
  const relatedDatamodels = useQueries({
    queries: selectedFields
      .filter((field) => Number.isInteger(field.related_datamodel_id))
      .map((field) => datamodelQuery(field.related_datamodel_id)),
  });
  const relatedDatamodelsReady = relatedDatamodels.every((q) => q.isSuccess);

  // most field types register their react-hook-form value directly at
  // "custom_fields.<slug>", but a few (mirroring DatamodelFormField's own
  // key choices) store it elsewhere - datamodel_item only ever writes
  // "<slug>_id"; image_select and image write both "<slug>_id" and "<slug>",
  // and image additionally writes "<slug>_alt" when alt tags are enabled
  const fieldValueKeys = (field) => {
    switch (field.field_type) {
      case 'datamodel_item':
        return [`${field.slug}_id`];
      case 'image_select':
        return [`${field.slug}_id`, field.slug];
      case 'image':
        return [
          `${field.slug}_id`,
          field.slug,
          ...(field.enable_alt_tag ? [`${field.slug}_alt`] : []),
        ];
      default:
        return [field.slug];
    }
  };

  // values to bulk-apply - blank slate, not tied to any one site's data
  const useFormReturn = useForm({ mode: 'onBlur' });
  const { handleSubmit, reset: resetValues } = useFormReturn;

  const saveMutation = useMutation(siteMutation(null));

  const onTemplateChange = (option) => {
    setSelectedTemplateId(option ? option.value : null);
    setSelectedSiteIds([]);
    setSelectedFieldSlugs([]);
    setSubmitMode('');
    setSubmitModeError(false);
    setShowConfirmation(false);
    resetValues();
  };

  const updateRow = (siteID, changes) => {
    setProgressRows((rows) =>
      rows.map((row) => (row.siteID === siteID ? { ...row, ...changes } : row)),
    );
  };

  const onPublish = async (data) => {
    if (!submitMode) {
      return;
    }

    // only ever apply the currently-selected fields - react-hook-form keeps
    // values around for fields that were selected earlier and since
    // deselected/unmounted, so pull just what's still selected; use each
    // field's own key(s) (see fieldValueKeys) rather than assuming every
    // field type stores its value at "custom_fields.<slug>"
    const bulkValues = {};
    selectedFields.forEach((field) => {
      fieldValueKeys(field).forEach((key) => {
        bulkValues[key] = data.custom_fields?.[key];
      });
    });

    setProgressRows(
      selectedSiteIds.map((siteID) => ({
        siteID,
        siteLabel: siteOptions.find((o) => o.value === siteID)?.label || siteID,
        status: 'pending',
        actionType: null,
      })),
    );
    setSubmittedModeLabel(
      SUBMIT_MODE_OPTIONS.find((o) => o.value === submitMode)?.label || '',
    );
    setShowConfirmation(false);
    setIsSubmitting(true);

    for (const siteID of selectedSiteIds) {
      updateRow(siteID, { status: 'saving' });
      try {
        // custom_fields is a plain dict column with no merge on the backend -
        // a partial PUT would blank out every field not included, so fetch
        // the site's current values (with_draft so a pending draft's values
        // are reflected, not the stale published ones) and merge the bulk
        // edit into them
        const currentSite = await queryClient.fetchQuery(
          siteQuery(siteID, {}, { with_draft: true }),
        );
        const merged = { ...(currentSite.custom_fields || {}), ...bulkValues };
        const hasDraft = !!currentSite.has_draft;
        // what this site's edit will actually end up as, given its own
        // current draft state - shown in the progress row so it's clear
        // publish_or_draft/publish_all don't behave identically per site
        const actionType =
          submitMode === SUBMIT_MODES.SAVE_DRAFT
            ? 'draft'
            : submitMode === SUBMIT_MODES.PUBLISH_OR_DRAFT
              ? hasDraft
                ? 'draft'
                : 'publish'
              : 'publish';
        updateRow(siteID, { actionType });

        if (submitMode === SUBMIT_MODES.SAVE_DRAFT) {
          // always create/update the draft, regardless of current state
          await saveMutation.mutateAsync({
            id: siteID,
            custom_fields: merged,
            draft_action: 'save',
          });
        } else if (submitMode === SUBMIT_MODES.PUBLISH_OR_DRAFT) {
          // leave an existing draft as a draft (just add our field to it);
          // only sites with no draft go live immediately
          await saveMutation.mutateAsync(
            hasDraft
              ? { id: siteID, custom_fields: merged, draft_action: 'save' }
              : { id: siteID, custom_fields: merged },
          );
        } else if (submitMode === SUBMIT_MODES.PUBLISH_ALL) {
          // publish_all: sites with a draft get the merged value saved into
          // it, then that draft (our field plus whatever else was pending)
          // is published; draft_action:'publish' ignores the request body,
          // so it has to be a separate call after the save
          if (hasDraft) {
            await saveMutation.mutateAsync({
              id: siteID,
              custom_fields: merged,
              draft_action: 'save',
            });
            await saveMutation.mutateAsync({
              id: siteID,
              draft_action: 'publish',
            });
          } else {
            await saveMutation.mutateAsync({
              id: siteID,
              custom_fields: merged,
            });
          }
        }

        queryClient.invalidateQueries({
          queryKey: ['sites', 'detail', siteID],
        });
        updateRow(siteID, { status: 'success' });
      } catch (error) {
        updateRow(siteID, { status: 'error' });
      }
    }
  };

  const onStartOver = () => {
    setIsSubmitting(false);
    setProgressRows([]);
    setSelectedTemplateId(null);
    setSelectedSiteIds([]);
    setSelectedFieldSlugs([]);
    setSubmitMode('');
    setSubmitModeError(false);
    setShowConfirmation(false);
    resetValues();
  };

  const validateSubmitMode = () => {
    if (submitMode) {
      setSubmitModeError(false);
      return true;
    }
    setSubmitModeError(true);
    return false;
  };

  if (isSubmitting) {
    return (
      <BulkUpdateProgress
        rows={progressRows}
        submitModeLabel={submittedModeLabel}
        onStartOver={onStartOver}
      />
    );
  }

  const submitModeWarning = SUBMIT_MODE_WARNINGS[submitMode];

  // gate any form submission (Enter key in a field, not just the Submit
  // button click) behind the confirmation step, since none of the buttons
  // below are type="submit" - see the Submit/Yes,submit button comment
  const onFormSubmit = (e) => {
    e.preventDefault();
    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }
    if (!validateSubmitMode()) {
      return;
    }
    handleSubmit(onPublish)(e);
  };

  return (
    <form onSubmit={onFormSubmit}>
      <div className="sb-form-item-formgroup sb-form-item-select">
        <label htmlFor="bulk-update-template">Templates</label>
        <div className="sb-form-item-content">
          {templatesIsPending ? (
            <Loader className="sb-loader" />
          ) : (
            <Select
              inputId="bulk-update-template"
              options={templateOptions}
              value={
                templateOptions.find((o) => o.value === selectedTemplateId) ||
                null
              }
              onChange={onTemplateChange}
              isClearable
              classNamePrefix="select"
              className="basic-select"
              placeholder="Select a template..."
              menuPortalTarget={document.body}
              styles={SELECT_MENU_PORTAL_STYLES}
            />
          )}
        </div>
      </div>

      {selectedTemplate && (
        <>
          <div className="sb-form-item-formgroup sb-form-item-multiselect">
            <label htmlFor="bulk-update-sites">Websites</label>
            <div className="sb-form-item-content">
              {sitesIsPending ? (
                <Loader className="sb-loader" />
              ) : (
                <Select
                  inputId="bulk-update-sites"
                  isMulti
                  options={siteOptions}
                  value={siteOptions.filter((o) =>
                    selectedSiteIds.includes(o.value),
                  )}
                  onChange={(vals) =>
                    setSelectedSiteIds(vals.map((v) => v.value))
                  }
                  classNamePrefix="select"
                  className="basic-multi-select"
                  placeholder="Select one or more websites..."
                  menuPortalTarget={document.body}
                  styles={SELECT_MENU_PORTAL_STYLES}
                />
              )}
            </div>
          </div>

          <div className="sb-form-item-formgroup sb-form-item-multiselect">
            <label htmlFor="bulk-update-fields">Fields</label>
            <div className="sb-form-item-content">
              {datamodelIsPending ? (
                <Loader className="sb-loader" />
              ) : (
                <Select
                  inputId="bulk-update-fields"
                  isMulti
                  options={fieldOptions}
                  value={fieldOptions.filter((o) =>
                    selectedFieldSlugs.includes(o.value),
                  )}
                  onChange={(vals) =>
                    setSelectedFieldSlugs(vals.map((v) => v.value))
                  }
                  classNamePrefix="select"
                  className="basic-multi-select"
                  placeholder="Select one or more fields..."
                  menuPortalTarget={document.body}
                  styles={SELECT_MENU_PORTAL_STYLES}
                />
              )}
            </div>
          </div>
        </>
      )}

      {selectedFieldSlugs.length > 0 && (
        <div
          className={
            `sb-form-item-formgroup sb-form-item-select ` +
            (submitModeError ? 'sb-form-item-error' : '')
          }
        >
          <label htmlFor="bulk-update-submit-mode">Submit as</label>
          <div className="sb-form-item-content">
            <select
              id="bulk-update-submit-mode"
              value={submitMode}
              aria-invalid={submitModeError ? 'true' : 'false'}
              onChange={(e) => {
                setSubmitMode(e.target.value);
                setSubmitModeError(false);
              }}
              required
            >
              <option value="">Select submit mode...</option>
              {SUBMIT_MODE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <HelpText
              message={
                SUBMIT_MODE_OPTIONS.find((o) => o.value === submitMode)
                  ?.helpText
              }
            />
            {submitModeError && <FormError message="This field is required" />}
          </div>
        </div>
      )}

      {selectedSiteIds.length > 0 &&
        selectedFields.length > 0 &&
        relatedDatamodelsReady && (
          <>
            <fieldset className="sb-form-item">
              <div className="sb-form-item-inner" style={{ marginBottom: 10 }}>
                <div className="sb-form-item-elements">
                  {selectedFields.map((field) => (
                    <DatamodelFormField
                      key={field.id}
                      datamodelField={field}
                      fieldPrefix="custom_fields."
                      relatedDatamodels={relatedDatamodels}
                      siteID={selectedSiteIds[0]}
                      useFormReturn={useFormReturn}
                    />
                  ))}
                </div>
              </div>
              {submitModeWarning && (
                <Message
                  messageStyle="warning"
                  fadeoutMessage={false}
                  isFormMessage={false}
                  title={submitModeWarning.title}
                  message={submitModeWarning.message}
                />
              )}
            </fieldset>
            <div className="sb-manage-content-button-bar">
              <div className="sb-flex">
                {showConfirmation ? (
                  <>
                    {/* deliberately type="button", not "submit" - see onClick */}
                    <button
                      type="button"
                      className="sb-btn sb-btn-delete-confirm"
                      onClick={() => {
                        if (!validateSubmitMode()) {
                          return;
                        }
                        handleSubmit(onPublish)();
                      }}
                    >
                      Yes, submit changes
                    </button>
                    <button
                      type="button"
                      className="sb-btn sb-btn-delete"
                      onClick={() => setShowConfirmation(false)}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="sb-btn sb-btn-save"
                    onClick={() => setShowConfirmation(true)}
                  >
                    Submit
                  </button>
                )}
              </div>
            </div>
          </>
        )}
    </form>
  );
};
